/**
 * The `quality` block on every JSON answer: how good the data behind it is, derived from the
 * report's own provenance (`report.sources`), so clients can tell a full lite build from an
 * NWS-only fallback instead of showing both as "the forecast".
 *
 *   { "tier": "full" | "edge" | "degraded", "sources": [...], "missing": [...] }
 *
 * - `edge`      the lite build the Functions compute (core.ts): the models blended, with the NWS
 *               alerts and the CAMS air-quality estimate. What every `/v2` answer normally carries.
 * - `full`      the complete engine report (ensembles, MET Norway, the full model roster) —
 *               served when a pre-baked full report stands in for a lite build.
 * - `degraded`  an acquired source failed, a core source is missing, or the last good copy stands in.
 *               Intentionally skipped/out-of-coverage inputs are not failures. Short cache life.
 *
 * `sources` are the ids of the sources that contributed (`ok` or `partial`); `missing` are the
 * ids absent from the forecast. Edge reports disclose deliberately skipped station,
 * ensemble and MET Norway inputs too. Sources outside geographic coverage are excluded.
 */

import type { WeatherReport } from '../shared/contract'

export type QualityTier = 'full' | 'edge' | 'degraded'

export interface Quality {
  tier: QualityTier
  sources: string[]
  missing: string[]
}

/** Without these the answer is a different product: the tier drops to `degraded`. */
const CORE_FULL = ['nws_forecast', 'openmeteo_models']
const CORE_EDGE = ['openmeteo_models']
/** Sources whose absence the edge tier discloses, including deliberately omitted inputs. */
const EXPECTED_EDGE = ['nws_forecast', 'nws_observations', 'nws_alerts', 'openmeteo_models', 'openmeteo_air', 'openmeteo_ensemble', 'metno']
/** What the full engine is expected to have, on top. */
const EXPECTED_FULL = ['nws_forecast', 'nws_observations', 'nws_alerts', 'openmeteo_models', 'openmeteo_air', 'openmeteo_minutely', 'openmeteo_ensemble', 'metno']

/** Pseudo-source in `missing` when the last good copy stands in for a failed rebuild. */
export const LIVE_BUILD = 'live_build'

export const isLiteReport = (r: Pick<WeatherReport, 'fusion'>) => /^lite\b/i.test(r.fusion.method)

export function qualityOf(r: WeatherReport, o: { stale?: boolean } = {}): Quality {
  const ok = new Set(r.sources.filter((s) => s.status === 'ok' || s.status === 'partial').map((s) => s.id))
  const skipped = new Set(r.sources.filter((s) => s.status === 'skipped').map((s) => s.id))
  const modelOnly = r.fusion.coverage === 'model_only'
  const lite = isLiteReport(r)
  const expected = (lite ? EXPECTED_EDGE : EXPECTED_FULL).filter((id) => !(modelOnly && id === 'nws_forecast'))
  const failed = r.sources.filter(s => s.status === 'unavailable' && !(modelOnly && s.id === 'nws_forecast'))
  const missing = [...new Set([...expected.filter((id) => !ok.has(id) && (lite || !skipped.has(id))), ...failed.map(s => s.id)])]
  const coreMissing = (lite ? CORE_EDGE : CORE_FULL).some((id) => !ok.has(id) && !(modelOnly && id === 'nws_forecast'))
  if (o.stale) missing.push(LIVE_BUILD)
  if (r.fusion.provenance?.policy === 'degraded') missing.push('seasonal_policy')
  const tier: QualityTier = o.stale || failed.length || coreMissing || r.fusion.provenance?.policy === 'degraded' ? 'degraded' : lite ? 'edge' : 'full'
  return { tier, sources: [...ok], missing }
}

/** Answers that come from one source and no fusion (alerts, places). */
export const singleSource = (sources: string[], missing: string[] = []): Quality => ({
  tier: missing.length ? 'degraded' : 'full',
  sources,
  missing,
})

export const isDegraded = (q: Quality) => q.tier === 'degraded'
